import React from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Quote, ArrowLeft } from "lucide-react";
import { EXAMPLES, EXAMPLE_LIST } from "@/data/examples";
import PlanContent from "@/components/report/PlanContent";

export default function ExampleReport() {
  const { slug } = useParams();
  const example = (slug && EXAMPLES[slug]) || EXAMPLES.kafe;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />

      <div className="max-w-4xl mx-auto px-6 lg:px-8 pt-10">
        <Link to="/eksempler" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="example-back">
          <ArrowLeft className="h-4 w-4" /> Alle eksempler
        </Link>
        <Badge variant="secondary" className="rounded-full bg-white border border-gray-200 text-slate-700" data-testid="example-badge">
          <Sparkles className="h-3.5 w-3.5 mr-1.5" /> {example.icon} {example.label}
        </Badge>
        <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
          {example.tagline}. Denne er fiktiv, men dekker alle seksjonene du får når du genererer din egen plan med IdeSetGo.
        </p>
      </div>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-10">
        <div data-testid="example-title-wrap"><PlanContent plan={example.data} /></div>

        {/* Related examples */}
        <div className="mt-16">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-4">Andre bransje-eksempler</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {EXAMPLE_LIST.filter((e) => e.slug !== example.slug).map((e) => (
              <Link
                key={e.slug}
                to={`/eksempel/${e.slug}`}
                className="rounded-2xl border border-gray-200 bg-white p-5 hover:shadow-md hover:-translate-y-1 transition-all duration-300 flex items-center gap-3"
                data-testid={`related-${e.slug}`}
              >
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center text-lg border ${e.color}`}>{e.icon}</div>
                <div>
                  <p className="text-slate-900 font-medium">{e.label}</p>
                  <p className="text-xs text-slate-500">{e.tagline}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-16 rounded-3xl bg-slate-900 text-white p-10 sm:p-16 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-white/5 blur-3xl"></div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300"><Quote className="h-4 w-4 inline mr-2" /> Klar til å lage din egen?</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-medium tracking-tight">Din versjon - skreddersydd på minutter.</h2>
          <p className="mt-4 text-slate-300 max-w-xl">Beskriv idéen din, så bygger IdeSetGo en lignende rapport tilpasset din bransje, marked og mål - klar for banken, Innovasjon Norge eller investorer.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button asChild size="lg" className="rounded-full h-12 px-7 bg-white text-slate-900 hover:bg-slate-100" data-testid="example-cta-start">
              <Link to="/register">Lag planen min <ArrowRight className="h-4 w-4 ml-2" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-7 border-white/20 text-white hover:bg-white/10 bg-transparent" data-testid="example-cta-pricing">
              <Link to="/#priser">Se priser</Link>
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
